import { ImagePaths } from '../../lib/imageUrls.js';

export const purviewRetentionAtScale = {

    id: 37,
    slug: 'purview-retention-at-scale',
    ogImage: '/og/courses/purview-retention-at-scale.jpg',
    courseCode: 'PURVIEW-RET-001',
    featured: true,
    title: 'Microsoft Purview Retention at Scale',
    subtitle: 'Design and operate enterprise-grade retention across Microsoft 365',
    valueProposition: 'Translate Privacy Act 1988 record-keeping obligations and OAIC guidance into a defensible Microsoft Purview retention programme that scales across SharePoint, OneDrive, Teams, and Exchange without breaking collaboration.',
    duration: '16 hours',
    students: 'New',
    level: 'Intermediate',
    image: ImagePaths.COURSE_2,
    shortDescription: 'PURVIEW-RET-001 — Purview retention at scale covering labels, auto-apply, and records management for governance leads under Privacy Act 1988 and OAIC APP 11.',
    price: {
      aud: 1199,
      currency: 'AUD',
      earlyBird: 899
    },
    stripeConfig: {
      priceId: 'price_1TbdY6Ku1nyc322UowpWmyuh',
      productId: 'prod_UapCiLilspQZUk'
    },

    snapshot: {
      whoItsFor: [
        'SharePoint and Teams governance leads operating Microsoft 365 at enterprise scale',
        'Records managers translating retention and disposal schedules into Purview policy',
        'Compliance officers aligning the organisation to Privacy Act 1988 and OAIC APP 11',
        'Microsoft 365 administrators rolling out retention labels, auto-apply, and records management'
      ],
      accreditation: 'Aligned to Microsoft Purview Data Lifecycle Management and Records Management learning paths'
    },

    logistics: {
      classSize: 'Two-day intensive — virtual cohort with hands-on labs and live Q&A',
      requirements: [
        'Working knowledge of Microsoft 365 (SharePoint, Teams, Exchange, OneDrive)',
        'Familiarity with Microsoft Purview compliance portal',
        'Access to a Microsoft 365 E5 (or Compliance add-on) tenant for labs',
        'Basic understanding of Australian privacy and records obligations'
      ]
    },

    overview: 'Microsoft Purview Retention at Scale is a two-day intensive that takes governance and compliance practitioners from retention fundamentals to an operating model that holds up under audit, e-discovery, and regulator scrutiny. You will design a defensible retention taxonomy for an Australian organisation, deploy auto-apply rules using KQL, sensitive information types, and trainable classifiers, and stand up records management with declaration, disposition review, and event-based retention. The programme is grounded in Microsoft Learn guidance for Purview Data Lifecycle Management and Records Management, OAIC retention and destruction advice, and the record-keeping expectations of the Privacy Act 1988. Microsoft Purview Retention at Scale is a standalone elective in the EDUC4TE Purview Training Track, complementing the Module 2 (Data Protection Practitioners) foundation and feeding records-management context into Module 6 (Enterprise Architecture).',

    outcomes: [
      'Explain the difference between retention labels and retention policies, including locations supported and policy precedence',
      'Apply Microsoft Purview conflict resolution principles (retain wins, longest retention wins, explicit over implicit, label over policy)',
      'Design a retention taxonomy mapped to Australian business functions, record classes, and Privacy Act 1988 obligations',
      'Configure auto-apply retention using KQL queries, sensitive information types, and trainable classifiers across SharePoint, OneDrive, Teams, and Exchange',
      'Stand up records management including label-based declaration, regulatory records, disposition review, and event-based retention',
      'Operate Purview audit, content search, and eDiscovery (Premium) workflows to prove retention is functioning',
      'Map controls to OAIC APP 11, Privacy Act 1988 record-keeping obligations, and the ACSC Information Security Manual data retention guidance',
      'Build a 90-day rollout plan covering pilot scoping, change management, and steady-state operations'
    ],

    audience: [
      'SharePoint and Teams governance leads',
      'Records managers and information governance specialists',
      'Compliance, privacy, and risk officers',
      'Microsoft 365 administrators with a compliance remit'
    ],

    prerequisites: [
      'Practical Microsoft 365 administration experience',
      'Exposure to the Microsoft Purview compliance portal',
      'Awareness of Australian privacy and records-keeping obligations'
    ],

    modules: [
      {
        number: 1,
        title: 'Retention concepts in Microsoft Purview',
        duration: '180 min',
        topics: [
          'Retention labels versus retention policies and when to use each',
          'Supported locations: SharePoint, OneDrive, Teams chats and channel messages, Exchange, Microsoft 365 Groups',
          'Policy precedence and conflict resolution (retain wins, longest wins, explicit over implicit, label over policy)',
          'Adaptive versus static policy scopes and what they mean operationally',
          'Reference: Microsoft Learn — Learn about retention policies and retention labels (updated 2025)'
        ]
      },
      {
        number: 2,
        title: 'Designing the retention taxonomy for an Australian organisation',
        duration: '180 min',
        topics: [
          'Mapping business functions and record classes to a Purview label taxonomy',
          'Aligning labels to Privacy Act 1988 record-keeping obligations and OAIC APP 11 destruction guidance',
          'Working with state and territory public records schedules where relevant',
          'Taxonomy patterns that avoid label sprawl and remain usable in Teams and SharePoint',
          'Reference: OAIC — Australian Privacy Principles guidelines, Chapter 11 (Security of personal information), current edition'
        ]
      },
      {
        number: 3,
        title: 'Auto-apply rules with KQL, sensitive info types, and trainable classifiers',
        duration: '240 min',
        topics: [
          'Authoring KQL queries for auto-apply policies across SharePoint and Exchange',
          'Choosing built-in versus custom sensitive information types for Australian data (TFN, Medicare, ABN, driver licence)',
          'Training and validating Purview trainable classifiers for organisation-specific content',
          'Pilot rollout, simulation mode, and measuring precision and recall before enforcement',
          'Reference: Microsoft Learn — Apply a retention label to content automatically (updated 2025)'
        ]
      },
      {
        number: 4,
        title: 'Records management: declaration, disposition, and event-based retention',
        duration: '180 min',
        topics: [
          'Records versus regulatory records and the implications of each',
          'Label-based and user-driven declaration patterns',
          'Disposition review workflows with multi-stage reviewers and proof of destruction',
          'Event-based retention for contracts, employee records, and project closeout',
          'Reference: Microsoft Learn — Learn about records management in Microsoft Purview (updated 2025)'
        ]
      },
      {
        number: 5,
        title: 'Audit, eDiscovery readiness, and Privacy Act 1988 alignment',
        duration: '180 min',
        topics: [
          'Using Purview Audit (Standard and Premium) to evidence retention activity',
          'Content search and eDiscovery (Premium) workflows that respect holds and retention',
          'Mapping the operating model to OAIC APP 11, Privacy Act 1988, and ACSC ISM data retention guidance',
          'Notifiable Data Breaches scheme implications when retained data is in scope',
          'Reference: OAIC — Notifiable Data Breaches scheme guidance; ACSC Information Security Manual (current release)',
          'Track positioning: handover into Module 4 (Insider Risk) and Module 6 (Enterprise Architecture) for end-to-end data protection coverage'
        ]
      }
    ],

    format: {
      type: 'Two-day intensive (virtual), or lunch-and-learn (16 x 1-hour sessions over 16 days)',
      includes: [
        '16 hours of instructor-led training across two consecutive days',
        'Hands-on labs in a Microsoft 365 compliance tenant',
        'Retention taxonomy worksheet aligned to Australian record classes',
        'KQL and sensitive information type pattern library',
        'Disposition review and event-based retention playbook',
        'Certificate of completion suitable for CPD claims'
      ]
    },

    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Register interest for the next two-day intensive — small cohort to support hands-on labs. Available as a private cohort for organisations enrolling teams through the EDUC4TE Purview Training Track.'
    },

    certification: 'Educ4te Certificate of Completion — Microsoft Purview Retention at Scale. Reinforces preparation for Microsoft SC-401 Information Security Administrator topics (SC-401 replaced the retired SC-400 on 31 May 2025).',

    faqs: [
      {
        question: 'Do I need a Microsoft 365 E5 tenant to follow the labs?',
        answer: 'Yes — auto-apply policies, trainable classifiers, and records management require a Microsoft 365 E5 licence or the Compliance add-on. A developer tenant with the Compliance add-on is sufficient for the labs.'
      },
      {
        question: 'How does this course align with the Privacy Act 1988 and OAIC guidance?',
        answer: 'Each module maps Purview controls to APP 11 (security of personal information), the Privacy Act 1988 record-keeping expectations, and current OAIC retention and destruction guidance. You leave with a control-to-obligation matrix you can take to audit.'
      },
      {
        question: 'Will we cover state and territory public records requirements?',
        answer: 'We focus on Commonwealth obligations and provide patterns for layering state and territory schedules (for example NSW State Records, PROV in Victoria) on top of a Purview taxonomy without duplicating labels.'
      },
      {
        question: 'How does retention interact with eDiscovery holds?',
        answer: 'We work through the precedence rules: holds preserve content regardless of retention, retention prevents premature deletion, and disposition review provides defensible destruction once obligations end. Labs include a hold-then-dispose scenario.'
      },
      {
        question: 'Is this suitable for records managers without deep Microsoft 365 experience?',
        answer: 'Yes — the cohort is intentionally mixed. Records managers contribute taxonomy and obligation expertise, administrators contribute tenancy knowledge, and the labs are scaffolded so both roles can participate.'
      },
      {
        question: 'Does completion count towards SC-401 preparation?',
        answer: 'The retention, records, and auto-apply content overlaps significantly with the Information Security Administrator (SC-401) exam objectives. SC-401 replaced the retired SC-400 on 31 May 2025. This course is not a replacement for full SC-401 study, but it accelerates it.'
      },
      {
        question: 'How does this fit into the EDUC4TE Purview Training Track?',
        answer: 'This is a standalone elective that complements the six-module EDUC4TE Purview Training Track rather than occupying a numbered slot. The core track runs Module 1 (Purview in a Day, Executive Edition), Module 2 (Data Protection Practitioners), Module 3 (Endpoint Security), Module 4 (Insider Risk Management), Module 5 (Purview for AI Governance), and Module 6 (Enterprise Architecture). Retention at Scale is best taken after Module 2, alongside the practitioner tier, and feeds records-management context into Module 6 (Enterprise Architecture).'
      },
      {
        question: 'Can this course be delivered as lunch-and-learn sessions?',
        answer: 'Yes. The course runs as sixteen one-hour sessions over sixteen days, with the same content and outcomes as the two-day format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on labs are spread across the sessions, so participants need access to the lab environment throughout. Contact us to arrange a schedule.'
      }
    ],

    australianContext: true,
    complianceFrameworks: ['OAIC APP 11', 'Privacy Act 1988', 'ACSC ISM (data retention)']
};
